import { useMemo } from "react";

import { matchRoutes, useLocation } from "react-router-dom";

import { RoutePaths } from "../config/routePaths";

export type ModuleRoot =
  | RoutePaths.USER_FEEDBACK_MODULE_ROOT
  | RoutePaths.CRASH_MODULE_ROOT
  | RoutePaths.USERS_MODULE_ROOT
  | RoutePaths.RELEASES_MODULE_ROOT;

export const MODULE_PAGE_NAMES: Record<ModuleRoot, string> = {
  [RoutePaths.USER_FEEDBACK_MODULE_ROOT]: "Feedback",
  [RoutePaths.CRASH_MODULE_ROOT]: "Crashes",
  [RoutePaths.USERS_MODULE_ROOT]: "Users",
  [RoutePaths.RELEASES_MODULE_ROOT]: "Releases",
};

/// Each module's ticket-list route, used to navigate back from a module-scoped screen like
/// Share Shake without hardcoding the destination at every call site.
/// Narrowed to just these literal members (rather than the whole `RoutePaths` enum) so
/// `generatePath` can still infer the right `:workspaceSlug/:appKey` param shape.
type ModuleListPath = RoutePaths.USER_FEEDBACK | RoutePaths.CRASH_REPORTS | RoutePaths.USERS | RoutePaths.RELEASES;

export const MODULE_LIST_PATHS: Record<ModuleRoot, ModuleListPath> = {
  [RoutePaths.USER_FEEDBACK_MODULE_ROOT]: RoutePaths.USER_FEEDBACK,
  [RoutePaths.CRASH_MODULE_ROOT]: RoutePaths.CRASH_REPORTS,
  [RoutePaths.USERS_MODULE_ROOT]: RoutePaths.USERS,
  [RoutePaths.RELEASES_MODULE_ROOT]: RoutePaths.RELEASES,
};

/// Only the modules whose tables can render a recommend-us interjected row have a Share Shake
/// route - Releases doesn't use interjected rows.
type ModuleShareShakePath =
  | RoutePaths.USER_FEEDBACK_SHARE_SHAKE
  | RoutePaths.CRASH_REPORT_SHARE_SHAKE
  | RoutePaths.USERS_SHARE_SHAKE;

export const MODULE_SHARE_SHAKE_PATHS: Partial<Record<ModuleRoot, ModuleShareShakePath>> = {
  [RoutePaths.USER_FEEDBACK_MODULE_ROOT]: RoutePaths.USER_FEEDBACK_SHARE_SHAKE,
  [RoutePaths.CRASH_MODULE_ROOT]: RoutePaths.CRASH_REPORT_SHARE_SHAKE,
  [RoutePaths.USERS_MODULE_ROOT]: RoutePaths.USERS_SHARE_SHAKE,
};

export const useCurrentPathRouteConfig = () => {
  const location = useLocation();

  const matchData = useMemo(
    () =>
      matchRoutes(
        Object.values(RoutePaths).map((path) => {
          return { path };
        }),
        location,
      ),
    [location],
  );

  const moduleRoot = useMemo((): ModuleRoot | undefined => {
    if (!matchData) return;

    const dynamicPath = matchData[0].route.path;

    if (!dynamicPath) return;

    if (dynamicPath.includes(RoutePaths.USER_FEEDBACK_MODULE_ROOT)) return RoutePaths.USER_FEEDBACK_MODULE_ROOT;
    if (dynamicPath.includes(RoutePaths.CRASH_MODULE_ROOT)) return RoutePaths.CRASH_MODULE_ROOT;
    if (dynamicPath.includes(RoutePaths.USERS_MODULE_ROOT)) return RoutePaths.USERS_MODULE_ROOT;
    if (dynamicPath.includes(RoutePaths.RELEASES_MODULE_ROOT)) return RoutePaths.RELEASES_MODULE_ROOT;
  }, [matchData]);

  const pageName = useMemo(() => {
    if (moduleRoot) return MODULE_PAGE_NAMES[moduleRoot];

    if (!matchData) return;

    const dynamicPath = matchData[0].route.path;

    if (!dynamicPath) return;

    if (dynamicPath.includes(RoutePaths.ADMINISTRATION_ROOT)) return "Administration";
    if (dynamicPath.includes(RoutePaths.ACCOUNT_ROOT)) return "Account";
  }, [matchData, moduleRoot]);

  const hasBorderBottom = useMemo(() => {
    if (!matchData) return;

    const dynamicPath = matchData[0].route.path;

    if (!dynamicPath) return;

    if (
      dynamicPath.includes(RoutePaths.USER_FEEDBACK_MODULE_ROOT) &&
      !dynamicPath.includes(RoutePaths.USER_FEEDBACK_DETAILS)
    )
      return false;
    if (
      dynamicPath.includes(RoutePaths.CRASH_MODULE_ROOT) &&
      !dynamicPath.includes(RoutePaths.CRASH_REPORT_OVERVIEW) &&
      !dynamicPath.includes(RoutePaths.CRASH_REPORT_EVENT)
    )
      return false;
    if (dynamicPath.includes(RoutePaths.USERS_MODULE_ROOT) && !dynamicPath.includes(RoutePaths.USER_DETAILS))
      return false;

    return !(dynamicPath.includes(RoutePaths.USER_FEEDBACK_LOGS) || dynamicPath.includes(RoutePaths.CRASH_REPORT_LOGS));
  }, [matchData]);

  return { pageName, hasBorderBottom, moduleRoot };
};
